import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlaneDeparture,
  faClock,
  faTrophy,
  faChartLine,
  faMedal,
  faLock,
  faUnlock,
  faStar,
  faAward,
  faSpinner,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import './CareerCardFramework.css';
import './CareerProgress.css';

const CareerProgress = () => {
  const [careerData, setCareerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('hours');

  useEffect(() => {
    fetchCareerProgress();
  }, []);

  const fetchCareerProgress = async () => {
    try {
      const response = await api.get('/career/career-progress');
      setCareerData(response.data);
    } catch (error) {
      console.error('Error fetching career progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMilestoneIcon = (level) => {
    if (level <= 2) return faPlaneDeparture;
    if (level <= 4) return faClock;
    if (level <= 6) return faTrophy;
    return faAward;
  };

  const getMilestoneColor = (level) => {
    if (level <= 2) return 'bronze';
    if (level <= 4) return 'silver';
    if (level <= 6) return 'gold';
    return 'platinum';
  };

  if (loading) {
    return (
      <div className="career-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>Cargando progreso de carrera...</p>
      </div>
    );
  }

  if (!careerData) {
    return (
      <div className="career-error">
        <p>Error al cargar el progreso de carrera</p>
      </div>
    );
  }

  const { currentRank, stats, nextMilestones, unlockedMilestones, availableMilestones } = careerData;

  const hoursMilestones = availableMilestones.filter(m => m.type === 'HOURS');
  const flightsMilestones = availableMilestones.filter(m => m.type === 'FLIGHTS');

  return (
    <div className="career-progress-container">
      <div className="career-header">
        <div className="career-title">
          <FontAwesomeIcon icon={faChartLine} className="career-icon" />
          <h1>Progreso de Carrera</h1>
        </div>

        <div className="career-stats-grid">
          <div className="career-stat-item">
            <FontAwesomeIcon icon={faClock} style={{ fontSize: '1.5rem', color: 'var(--accent-primary)', marginBottom: '0.5rem' }} />
            <span className="career-stat-value">{Math.floor(stats.totalHours)}h</span>
            <span className="career-stat-label">Horas Totales</span>
          </div>
          <div className="career-stat-item">
            <FontAwesomeIcon icon={faPlaneDeparture} style={{ fontSize: '1.5rem', color: 'var(--accent-primary)', marginBottom: '0.5rem' }} />
            <span className="career-stat-value">{stats.totalFlights}</span>
            <span className="career-stat-label">Vuelos Totales</span>
          </div>
          <div className="career-stat-item">
            <FontAwesomeIcon icon={faStar} style={{ fontSize: '1.5rem', color: '#f59e0b', marginBottom: '0.5rem' }} />
            <span className="career-stat-value" style={{ color: '#f59e0b' }}>{stats.gamificationPoints}</span>
            <span className="career-stat-label">Puntos de Gamificación</span>
          </div>
        </div>
      </div>

      {currentRank && (
        <div className="current-rank-display">
          {currentRank.img && (
            <img
              src={currentRank.img}
              alt={currentRank.name}
              className="rank-medal"
              style={{ height: '100px', width: 'auto' }}
            />
          )}
          <div className="rank-info">
            <h2>Rango Actual</h2>
            <h3>{currentRank.name}</h3>
            <p>Mínimo {currentRank.hours} horas de vuelo</p>
          </div>
        </div>
      )}

      <div className="career-section">
        <div className="career-section-header">
          <h2 className="career-section-title">
            <FontAwesomeIcon icon={faChevronRight} />
            Próximos Objetivos
          </h2>
        </div>
        <div className="career-cards-grid cols-2">
          {nextMilestones.hours && (
            <div className="career-card">
              <div className="career-card-glow" />
              <div className="career-card-header">
                <div className="career-card-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #60a5fa)' }}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <span className="career-card-badge">HORAS DE VUELO</span>
              </div>
              <div className="career-card-content">
                <h3 className="career-card-title">{nextMilestones.hours.name}</h3>
                <p className="career-card-description">{nextMilestones.hours.description}</p>
              </div>
              <div className="career-progress-wrapper">
                <div className="career-progress-bar">
                  <div
                    className="career-progress-fill"
                    style={{ 
                      width: `${nextMilestones.hours.progress}%`,
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                    }}
                  />
                </div>
                <div className="career-progress-text">
                  <span className="career-progress-current">
                    {Math.floor(nextMilestones.hours.currentValue)} horas
                  </span>
                  <span className="career-progress-target">
                    / {nextMilestones.hours.requiredHours} horas
                  </span>
                </div>
              </div>
              <div className="career-card-footer">
                <div className="career-card-points">
                  <FontAwesomeIcon icon={faStar} />
                  <span>+{nextMilestones.hours.points} puntos</span>
                </div>
              </div>
            </div>
          )}

          {nextMilestones.flights && (
            <div className="career-card">
              <div className="career-card-glow" />
              <div className="career-card-header">
                <div className="career-card-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>
                  <FontAwesomeIcon icon={faPlaneDeparture} />
                </div>
                <span className="career-card-badge">NÚMERO DE VUELOS</span>
              </div>
              <div className="career-card-content">
                <h3 className="career-card-title">{nextMilestones.flights.name}</h3>
                <p className="career-card-description">{nextMilestones.flights.description}</p>
              </div>
              <div className="career-progress-wrapper">
                <div className="career-progress-bar">
                  <div
                    className="career-progress-fill"
                    style={{ 
                      width: `${nextMilestones.flights.progress}%`,
                      background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                    }}
                  />
                </div>
                <div className="career-progress-text">
                  <span className="career-progress-current">
                    {nextMilestones.flights.currentValue} vuelos
                  </span>
                  <span className="career-progress-target">
                    / {nextMilestones.flights.requiredFlights} vuelos
                  </span>
                </div>
              </div>
              <div className="career-card-footer">
                <div className="career-card-points">
                  <FontAwesomeIcon icon={faStar} />
                  <span>+{nextMilestones.flights.points} puntos</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="milestones-tabs">
        <div className="tabs-header">
          <button
            className={`tab-button ${selectedTab === 'hours' ? 'active' : ''}`}
            onClick={() => setSelectedTab('hours')}
          >
            <FontAwesomeIcon icon={faClock} />
            Hitos por Horas
          </button>
          <button
            className={`tab-button ${selectedTab === 'flights' ? 'active' : ''}`}
            onClick={() => setSelectedTab('flights')}
          >
            <FontAwesomeIcon icon={faPlaneDeparture} />
            Hitos por Vuelos
          </button>
        </div>

        <div className="milestones-content">
          <div className="career-cards-grid cols-3">
            {(selectedTab === 'hours' ? hoursMilestones : flightsMilestones).map((milestone, index) => {
              const isUnlocked = unlockedMilestones.some(um => um.id === milestone.id);
              const isNext = selectedTab === 'hours' 
                ? nextMilestones.hours?.id === milestone.id
                : nextMilestones.flights?.id === milestone.id;
              
              const rarityMap = {
                1: 'common',
                2: 'common',
                3: 'rare',
                4: 'rare',
                5: 'epic',
                6: 'epic',
                7: 'legendary',
                8: 'legendary'
              };
              
              return (
                <div
                  key={milestone.id}
                  className={`career-card ${
                    isUnlocked ? 'completed' : 'locked'
                  } rarity-${rarityMap[milestone.level] || 'common'} ${
                    isNext ? 'active' : ''
                  }`}
                >
                  {isNext && <div className="career-card-ribbon">PRÓXIMO</div>}
                  <div className="career-card-glow" />
                  
                  <div className="career-card-header">
                    <div className={`career-card-icon ${getMilestoneColor(milestone.level)}`}>
                      <FontAwesomeIcon
                        icon={getMilestoneIcon(milestone.level)}
                      />
                      {isUnlocked && (
                        <span style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: 'var(--background-primary)',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FontAwesomeIcon
                            icon={faUnlock}
                            style={{ color: '#10b981', fontSize: '0.75rem' }}
                          />
                        </span>
                      )}
                      {!isUnlocked && (
                        <span style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: 'var(--background-primary)',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FontAwesomeIcon
                            icon={faLock}
                            style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}
                          />
                        </span>
                      )}
                    </div>
                    <div className="career-card-badge">
                      NIVEL {milestone.level}
                    </div>
                  </div>
                  
                  <div className="career-card-content">
                    <h4 className="career-card-title">{milestone.name}</h4>
                    <p className="career-card-description">{milestone.description}</p>
                    
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.75rem', 
                      marginTop: '1rem',
                      padding: '0.5rem',
                      background: 'var(--background-secondary)',
                      borderRadius: '0.5rem',
                      justifyContent: 'center'
                    }}>
                      {milestone.requiredHours && (
                        <span style={{ 
                          fontSize: '0.9rem', 
                          color: 'var(--text-primary)',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <FontAwesomeIcon icon={faClock} style={{ color: 'var(--accent-primary)' }} />
                          <strong>{milestone.requiredHours.toLocaleString()}</strong> horas
                        </span>
                      )}
                      {milestone.requiredFlights && (
                        <span style={{ 
                          fontSize: '0.9rem', 
                          color: 'var(--text-primary)',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <FontAwesomeIcon icon={faPlaneDeparture} style={{ color: 'var(--accent-primary)' }} />
                          <strong>{milestone.requiredFlights}</strong> vuelos
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="career-card-footer">
                    <div className="career-card-points">
                      <FontAwesomeIcon icon={faStar} />
                      <span>{milestone.points} puntos</span>
                    </div>
                    {isUnlocked && (
                      <FontAwesomeIcon 
                        icon={faMedal} 
                        style={{ color: '#f59e0b', fontSize: '1.25rem' }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{
        background: 'var(--background-primary)',
        borderRadius: '1rem',
        padding: '2rem',
        marginTop: '2rem',
        border: '1px solid var(--background-secondary)'
      }}>
        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1.5rem 0', fontSize: '1.25rem' }}>
          Resumen de Logros
        </h3>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{
            flex: '1',
            minWidth: '200px',
            background: 'var(--background-secondary)',
            padding: '1rem',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <FontAwesomeIcon icon={faMedal} style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }} />
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                {unlockedMilestones.length}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Hitos Desbloqueados
              </div>
            </div>
          </div>
          <div style={{
            flex: '1',
            minWidth: '200px',
            background: 'var(--background-secondary)',
            padding: '1rem',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <FontAwesomeIcon icon={faStar} style={{ fontSize: '1.5rem', color: '#f59e0b' }} />
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                {unlockedMilestones.reduce((sum, m) => sum + (m.points || 0), 0)}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Puntos Ganados
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerProgress;